import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomToolTip from "../../../../components/CustomTooltip";
import { Toast } from "../../../../components/Toast";

export default function AWSDrawer({
  isOpen,
  account,
  handleUpdateAccount,
  handleDeleteAccount,
  handleUpdateNickname,
  closeDrawer,
  awsRegionOptions,
  guideMode = false,
}) {
  const colors = useColors();
  const [localAccount, setLocalAccount] = useState({
    regions: [],
    account_id: "",
    nickname: "",
    role_ARN: "",
  });
  const [regionList, setRegionList] = useState([]);
  const [newRegion, setNewRegion] = useState("");
  const [editNickName, setEditNickName] = useState(false);

  useEffect(() => {
    if (account) {
      setLocalAccount(account);
      setRegionList(account.regions);
    }
  }, [account]);

  const deleteRegion = (region) => {
    if (account.regions.length === 1) {
      Toast.warning("At least one region is required");
      return;
    }
    const newRegions = account.regions.filter((r) => r !== region);
    const tempAccount = { ...account, regions: newRegions };
    setLocalAccount(tempAccount);
    handleUpdateAccount(tempAccount);
  };

  const addRegion = () => {
    if (newRegion === null) {
      return; // Do not add empty region
    }
    const updatedRegionList = [...regionList, newRegion];
    setRegionList(updatedRegionList);
    const tempAccount = { ...account, regions: updatedRegionList };
    setLocalAccount(tempAccount);
    handleUpdateAccount(tempAccount);
    setNewRegion("");
  };

  const handleEditNickname = () => {
    setEditNickName(!editNickName);
  };
  const handleCancelEditNickname = () => {
    setEditNickName(false);
  };
  const handleSaveEditNickname = () => {
    setEditNickName(false);
    handleUpdateNickname(localAccount.nickname);
  };

  return (
    <Drawer
      PaperProps={{ style: { borderRadius: "8px 0 0 0" } }}
      anchor="right"
      open={isOpen}
      onClose={closeDrawer}
    >
      <Box
        sx={{
          width: "600px",
          height: "100%",
          backgroundColor: colors.grey_50,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px",
            borderBottom: `1px solid ${colors.grey_300}`,
          }}
        >
          {editNickName ? (
            <TextField
              value={localAccount.nickname}
              onChange={(e) =>
                setLocalAccount({ ...localAccount, nickname: e.target.value })
              }
              size="small"
              fullWidth
            />
          ) : (
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {localAccount.nickname}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "flex-end",
            }}
          >
            {editNickName ? (
              <>
                <IconButton
                  onClick={handleSaveEditNickname}
                  color="primary"
                  size="small"
                  disabled={guideMode}
                >
                  <SaveIcon />
                </IconButton>
                <IconButton
                  onClick={handleCancelEditNickname}
                  color="secondary"
                  size="small"
                  disabled={guideMode}
                >
                  <CancelIcon />
                </IconButton>
              </>
            ) : (
              <IconButton
                onClick={handleEditNickname}
                size="small"
                disabled={guideMode}
              >
                <EditIcon />
              </IconButton>
            )}

            <IconButton
              onClick={() => handleDeleteAccount(account)}
              color="error"
              size="small"
              disabled={guideMode}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ padding: "20px", flexGrow: 1, overflow: "auto" }}>
          {/* Role ARN */}
          <Box sx={{ marginBottom: "20px" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Role ARN
            </Typography>
            <Typography
              variant="body1"
              sx={{
                padding: "10px",
                backgroundColor: colors.grey_100,
                borderRadius: "8px",
              }}
            >
              {localAccount.role_ARN}
            </Typography>
          </Box>

          {/* Regions */}
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Regions to scan
          </Typography>

          <Grid container spacing={2} sx={{ marginTop: "10px" }}>
            <Grid item xs={7}>
              <Autocomplete
                options={awsRegionOptions.filter(
                  (region) => !localAccount.regions.includes(region.code)
                )}
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Region name"
                    variant="outlined"
                    fullWidth
                  />
                )}
                onChange={(event, newValue) =>
                  setNewRegion(newValue?.code || "")
                }
              />
            </Grid>
            <Grid item xs={5} sx={{ display: "flex", alignItems: "center" }}>
              <SecondaryButton
                variant="contained"
                sx={{
                  height: "40px",
                  textTransform: "none",
                  borderRadius: "8px",
                }}
                onClick={addRegion}
                disabled={!newRegion || guideMode}
              >
                Add Region
              </SecondaryButton>
            </Grid>
          </Grid>

          {/* Regions List */}
          <List sx={{ marginTop: "20px" }}>
            {localAccount.regions.map((region, index) => (
              <ListItem
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: `1px solid ${colors.grey_300}`,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    backgroundColor: colors.grey_100,
                    padding: "8px 12px",
                    borderRadius: "8px",
                    flexGrow: 1,
                  }}
                >
                  {`${region} (${
                    awsRegionOptions.find((r) => r.code === region)?.name ||
                    "Unknown"
                  })`}
                </Typography>
                <CustomToolTip
                  title={
                    localAccount.regions.length === 1
                      ? "At least one region is required"
                      : "This region will be removed and no longer scanned"
                  }
                  placement="top"
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      sx={{
                        marginLeft: "10px",
                        textTransform: "none",
                        borderRadius: "8px",
                      }}
                      onClick={() => deleteRegion(region)}
                      disabled={localAccount.regions.length === 1 || guideMode}
                    >
                      Remove
                    </Button>
                  </span>
                </CustomToolTip>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </Drawer>
  );
}
